import { ContentUsageFormEventsFieldQuery } from "@/content-usage/buttons/events/__generated__/ContentUsageFormEventsFieldQuery.graphql"
import { useActiveOrganization } from "@/core/context/ActiveOrganizationContext"
import { useActiveProduct } from "@/core/context/ActiveProductContext"
import { OrganizationOccurrencesDatetimeFilter } from "@/organization/occurrence/__generated__/OrganizationOccurrenceListNextPaginationQuery.graphql"
import OccurrenceNameAndTime from "@/product/course/event/components/OccurrenceNameAndTime"
import { GlobalID } from "@/relay/RelayTypes"
import Relay from "@/relay/relayUtils"
import useUserTimezone from "@/user/util/useUserTimezone"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import DaySquare from "@components/date/day-square/DaySquare"
import {
  DiscoIcon,
  DiscoIconButton,
  DiscoInputSkeleton,
  DiscoSection,
  DiscoText,
  DiscoTooltip,
} from "@disco-ui"
import DiscoMultiSelect from "@disco-ui/select/DiscoMultiSelect"
import { useTheme } from "@material-ui/core"
import { ArrayUtils } from "@utils/array/arrayUtils"
import { DATE_FORMAT } from "@utils/time/timeConstants"
import { formatDateWithOptions } from "@utils/time/timeUtils"
import { isBefore } from "date-fns"
import { observer } from "mobx-react-lite"
import { useMemo, useState } from "react"
import { graphql } from "react-relay"

const OCCURRENCES_PER_LOAD = 10

type Filter = OrganizationOccurrencesDatetimeFilter | "all"

const OCCURRENCES_FILTERS: { value: Filter; title: string }[] = [
  { value: "upcoming", title: "Upcoming" },
  { value: "past", title: "Past" },
  { value: "all", title: "All Events" },
]

type Props = {
  values: GlobalID[]
  onSelect: (occurrenceIds: GlobalID[]) => void
  onRemove: (occurrenceId: GlobalID) => void
}

function ContentUsageFormEventsField(props: Props) {
  const { values, onSelect, onRemove } = props
  const classes = useStyles()
  const activeProduct = useActiveProduct()
  const activeOrganization = useActiveOrganization()
  const userTimezone = useUserTimezone()
  const theme = useTheme()
  const [filter, setFilter] = useState<Filter>("upcoming")

  const { data, pagination } =
    Relay.useRefetchablePaginationQuery<ContentUsageFormEventsFieldQuery>(
      graphql`
        query ContentUsageFormEventsFieldQuery(
          $id: ID!
          $selectedIds: [ID!]
          $productId: ID!
          $first: Int!
          $after: String
          $search: String
          $datetimeFilter: OrganizationOccurrencesDatetimeFilter
          $orderBy: OccurrencesOrderBy
          $direction: OrderDirection
        ) {
          organization: node(id: $id) {
            ... on Organization {
              id
              selectedOccurrences: occurrences(
                ids: $selectedIds
                includedProductIds: [$productId]
                orderBy: $orderBy
                direction: $direction
                first: null
              ) @connection(key: "ContentUsageFormEventsField__selectedOccurrences") {
                edges {
                  node {
                    ...ContentUsageFormEventsField_OccurrenceFragment @relay(mask: false)
                  }
                }
              }
              occurrences(
                first: $first
                after: $after
                search: $search
                status: published
                visibility: [cohort, public, member_group]
                includedProductIds: [$productId]
                datetimeFilter: $datetimeFilter
                orderBy: $orderBy
                direction: $direction
              ) @connection(key: "ContentUsageFormEventsField__occurrences") {
                edges {
                  node {
                    id
                    ...ContentUsageFormEventsField_OccurrenceFragment @relay(mask: false)
                  }
                }
                pageInfo {
                  hasNextPage
                  hasPreviousPage
                  startCursor
                  endCursor
                }
              }
            }
          }
        }
      `,
      {
        id: activeOrganization?.id || "",
        selectedIds: values,
        productId: activeProduct?.id || "",
        datetimeFilter: filter === "all" ? null : filter,
        orderBy: "start_date",
        direction: filter === "past" ? "DESC" : "ASC",
        first: OCCURRENCES_PER_LOAD,
      },
      { fetchPolicy: "network-only", connectionName: "organization.occurrences" }
    )

  const organization = data?.organization
  const selectedOccurrences = Relay.connectionToArray(organization?.selectedOccurrences)
  const occurrences = useMemo(() => {
    const os = Relay.connectionToArray(organization?.occurrences)
    const osIds = new Set(os.map((o) => o.id))

    // Add selected occurrences if they are not already in the list
    for (const selectedOccurrence of selectedOccurrences) {
      if (osIds.has(selectedOccurrence.id)) continue
      os.push(selectedOccurrence)
    }

    return os
  }, [organization?.occurrences, selectedOccurrences])

  const occurrencesById = ArrayUtils.mapBy(occurrences, "id")

  const options = occurrences
    .filter((occurrence) => !values.includes(occurrence.id))
    .map((occurrence) => ({
      title: occurrence.content?.name || "",
      value: occurrence.id,
      searchable: [
        occurrence.content?.name,
        formatDateWithOptions({
          timeZone: userTimezone,
          format: DATE_FORMAT.DEFAULT_DATE_WITH_SHORT_TIME_FORMAT_WITH_OFFSET_AND_AT,
        })(new Date(occurrence.datetimeRange[0])),
      ],
    }))

  // Sort occurrences by date in order of upcoming->asc, then past->desc, for select dropdown
  const sortedOccurrences = useMemo(() => {
    const upcoming = [],
      past = [],
      now = new Date()

    for (const value of values) {
      const occurrence = occurrencesById[value]
      if (!occurrence) continue

      if (isBefore(new Date(occurrence.datetimeRange[1]), now)) {
        past.push(occurrence)
      } else {
        upcoming.unshift(occurrence)
      }
    }

    return upcoming.concat(past)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [occurrencesById, values])

  return (
    <>
      <DiscoText variant={"body-sm"} color={"text.secondary"} marginBottom={1}>
        {"Only published events may be linked to a Module."}
      </DiscoText>
      <DiscoMultiSelect
        testid={"ContentUsageFormEventsField.select"}
        pagination={pagination}
        options={options}
        values={[]}
        hideTags
        filters={OCCURRENCES_FILTERS}
        selectedFilter={filter}
        setFilter={handleFilterChange}
        onChange={handleSelect}
        renderOption={(option) => {
          const occurrence = occurrencesById[option.value]
          const startTime = new Date(occurrence.datetimeRange[0])
          return (
            <div className={classes.selectOption}>
              <DaySquare testid={"list-event-day"} date={startTime} />
              <OccurrenceNameAndTime
                occurrenceKey={occurrence}
                variant={"compact"}
                testid={`ContentUsageFormEventsField.option`}
              />
            </div>
          )
        }}
        placeholder={"Type to search events"}
      />

      {sortedOccurrences.map((occurrence, i) => {
        return (
          <DiscoSection
            key={occurrence.id}
            className={classes.occurrence}
            testid={`ContentUsageFormEventsField.occurrence-${i}`}
            groovyDepths={"insideCard"}
            padding={1.5}
          >
            <DaySquare
              testid={"list-event-day"}
              date={new Date(occurrence.datetimeRange[0])}
              customClassName={classes.daySquare}
            />
            <div className={classes.occurrenceContent}>
              <OccurrenceNameAndTime occurrenceKey={occurrence} variant={"compact"} />
              {occurrence.visibility === "member_group" && (
                <DiscoTooltip
                  placement={"right"}
                  content={
                    "This is a private event and only accessible to certain sub groups"
                  }
                >
                  <div
                    className={classes.warningContainer}
                    data-testid={`ContentUsageFormEventsField.occurrence-${i}-private-warning`}
                  >
                    <DiscoIcon
                      icon={"warning"}
                      width={16}
                      height={16}
                      color={theme.palette.groovy.yellow[600]}
                    />
                    <DiscoText variant={"body-xs"} fontStyle={"italic"}>
                      {"Private Event"}
                    </DiscoText>
                  </div>
                </DiscoTooltip>
              )}
            </div>
            <DiscoIconButton
              onClick={() => handleRemove(occurrence.id)}
              testid={`ContentUsageFormEventsField.occurrence-${i}.remove`}
            >
              <DiscoIcon icon={"close"} />
            </DiscoIconButton>
          </DiscoSection>
        )
      })}
    </>
  )

  async function handleFilterChange(selectedFilter: Filter) {
    setFilter(selectedFilter)

    await pagination.refetch({
      id: activeOrganization?.id || "",
      selectedIds: values,
      productId: activeProduct?.id || "",
      datetimeFilter: selectedFilter === "all" ? null : selectedFilter,
      orderBy: "start_date",
      direction: selectedFilter === "past" ? "DESC" : "ASC",
      search: pagination.variables.search,
    })
  }

  function handleSelect(ids: GlobalID[]) {
    onSelect(ids)
  }

  function handleRemove(id: GlobalID) {
    onRemove(id)
  }
}

const useStyles = makeUseStyles((theme) => ({
  occurrence: {
    marginTop: theme.spacing(1.5),
    "&:first-child": {
      marginTop: 0,
    },
    display: "flex",
    width: "100%",
    alignItems: "flex-start",
    minWidth: 0,
  },
  occurrenceContent: {
    flex: 1,
    minWidth: 0,
  },
  daySquare: {
    flexShrink: 0,
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  selectOption: {
    display: "flex",
    width: "100%",
    alignItems: "center",
    justifyContent: "flex-start",
    marginBottom: theme.spacing(1),
  },
  warningContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1),
    marginLeft: theme.spacing(1.5),
    width: "fit-content",
    maxWidth: "100%",
  },
}))

function ContentUsageFormEventsFieldSkeleton() {
  return (
    <>
      <DiscoText variant={"body-sm"} color={"text.secondary"} marginBottom={1}>
        {"Only published events may be linked to a Module."}
      </DiscoText>
      <DiscoInputSkeleton />
    </>
  )
}

export default Relay.withSkeleton({
  component: observer(ContentUsageFormEventsField),
  skeleton: ContentUsageFormEventsFieldSkeleton,
})

// eslint-disable-next-line no-unused-expressions
graphql`
  fragment ContentUsageFormEventsField_OccurrenceFragment on Occurrence {
    id
    status
    datetimeRange
    visibility
    content {
      name
    }
    ...OccurrenceNameAndTimeFragment
  }
`
